import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DataFieldGroupModel } from '../data-field-group-model';
import { SideListModel } from '@fi-app/utilities/side-list/side-list.model';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { DataFieldService } from '../data-field.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { DataFieldModel } from '../data-field.model';
import { SideListComponent } from '@fi-app/utilities/side-list/side-list.component';
import { DataFieldGroupEditItemService } from './data-field-group-edit.service';
import { DataFieldPropertyType } from '../data-field-property-type';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiSideListType } from '@fi-app/utilities/side-list/side-list-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { DataFieldHelperService } from '@fi-app/data-field/data-field-helper.service';
import { IDataFieldSection } from '@fi-app/data-field/data-field';
import { isDefined } from '@fi-shared/utils';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
    selector: 'fi-data-field-group-edit',
    templateUrl: './data-field-group-edit.component.html',
    styleUrls: ['./data-field-group-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-data-field-group-edit' },
    standalone: false,
})
export class DataFieldGroupEditComponent implements OnInit {
    @Input() dataFieldGroup: DataFieldGroupModel;
    @ViewChild('languageList', { static: false }) languageList: SideListComponent;

    EditTabs: EditTabsModel;
    activeItem: number;
    ccmSideList: SideListModel;
    dataFieldSections: IDataFieldSection[];

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private dataFieldService: DataFieldService,
        private dataFieldHelperService: DataFieldHelperService,
        private fiLoadingService: LoadingService,
        private appTextService: AppTextService,
        private statisticService: StatisticService,
        private dataFieldGroupEditItemService: DataFieldGroupEditItemService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiDialogsService: FiDialogsService,
        private stateService: FiStateService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.dataFieldSections = this.dataFieldHelperService.getDataFieldSections(
            this.dataFieldGroup.ObjectType,
            this.dataFieldGroup.DataFields,
        );
        this.setPageHeaderData();
        this.setListDataSettings();
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.DataFieldGroup, this.dataFieldGroup.Id);
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(this.getDataFieldGroupName(), this.appTextService.get('back'));
    }

    private setListDataSettings() {
        this.ccmSideList = new SideListModel(
            FiSideListType.Translations,
            this.userDataService.applicationLanguage,
            this.userDataService.userData.PreferredContentLanguage,
        );
        this.ccmSideList.Data = this.contentLanguagesService.GetApplicationLanguages();
        this.ccmSideList.DefaultItem = this.customerDataService.customerData.DefaultApplicationLanguageType;

        this.saveChangesWarningService.start(() => this.dataFieldSections);
    }

    getDataFieldsHeaderText(dataFieldGroup: DataFieldGroupModel): string {
        if (dataFieldGroup.ObjectType == ObjectTypes.BusinessUnit) return 'data_field.header_object_unit';

        if (dataFieldGroup.ObjectType == ObjectTypes.User) return 'data_field.header_object_user';

        console.error(`unknown DataFieldGroup ObjectType ${dataFieldGroup.ObjectType}`, this.dataFieldGroup);
    }

    private getDataFieldGroupName(): string {
        return this.dataFieldService.getDataFieldGroupName(this.dataFieldGroup.ObjectType);
    }

    saveClick() {
        const dataFieldsToSave: DataFieldModel[] = [];
        this.dataFieldSections.forEach((section) => dataFieldsToSave.push(...section.dataFields));
        this.dataFieldGroup.DataFields = dataFieldsToSave;
        this.fiLoadingService.trackPromise(this.dataFieldService.saveDataFieldGroup(this.dataFieldGroup)).then(() => {
            this.saveChangesWarningService.stop();
            this.stateService.go(FiStateNames.OtherSettings.List);
        });
    }

    addFieldClick() {
        const item = new DataFieldModel(this.contentLanguagesService);
        item.PropertyType = DataFieldPropertyType.Custom;
        this.dataFieldGroupEditItemService.handleEditItem(item, this.languageList.getSelectedItem()).then(() => {
            const targetDataFieldSection = this.dataFieldSections.find((section) => section.isAddingEnabled);
            if (isDefined(targetDataFieldSection)) {
                targetDataFieldSection.dataFields.push(item);
                this.cdr.detectChanges();
            }

            /*const newName = item.Names.find(n => n.ContentLanguageId == this.activeItem);
            if (newName) {
                item.Names = this.contentLanguagesService.GetApplicationLanguages().map(lang => {
                    return new ContentLanguageLabelModel(lang.Id, newName.Value)
                });
            }*/
        });
    }

    isShowDeleteButton(item: DataFieldModel): boolean {
        return item.PropertyType === DataFieldPropertyType.Custom;
    }

    deleteCustomField(dataFieldToDelete: DataFieldModel, dataFields: DataFieldModel[]) {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('data_field.delete_confirm_header'),
                messageText: this.appTextService.get('data_field.delete_confirm'),
            })
            .onYesButtonClick(() => {
                dataFields.splice(dataFields.indexOf(dataFieldToDelete), 1);
                this.cdr.detectChanges();
            });
    }

    onItemDrop($event: CdkDragDrop<DataFieldModel>, dataFields: DataFieldModel[]) {
        moveItemInArray(dataFields, $event.previousIndex, $event.currentIndex);
        this.cdr.detectChanges();
    }
}
